<template>
    <div>
        <p class="company-info">Account Manager: {{ companyInfos.accountManager }}</p>
        <p class="company-info">{{ companyInfos.totalActiveUsers }} utilisateurs actifs</p>
    </div>
    <div id="company-category-settings"> 
        <h1>
            <span class="page-title">Cacher des catégories</span>
            <span class="lg:tooltip title-tooltip" data-tip="Les catégories cochées seront cachées pour les utilisateurs de cette société cliente.">
                <i class="pi pi-exclamation-circle"
                    style="color: orange; font-size: 1.5rem">
                </i>
            </span>
        </h1>
        <div>
            <Tree :selectionKeys="selectedCategoriesKeys" :expandedKeys="expandedCategoriesKeys"
                :value="categoriesLevel1Nodes" :loading="isLoadingCategories" selectionMode="checkbox"
                @update:selectionKeys="onUpdateSelectedCategories($event)" class="w-full md:w-30rem"></Tree>
            <Button class="btn btn-primary p-2 ps-2 pe-2" :disabled="!isInitCategoriesStateChanges" id="saveCategoriesBtn"
                label="Enregistrer les modifications" aria-label="Enregistrer les modifications"
                @click="saveCategories($event)"></Button>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch } from "vue";
import ApiService from "@/_services/api.service";
import swal from "../../../_helpers/swal";
import Tree from "primevue/tree";
import Button from "primevue/button";
import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";

type SelectedTreeNodePrimeVueEventType = {
    [key: string]: {
        checked: boolean;
        partialChecked: boolean;
    };
};

type SelectedCategoryType = {
    [key: string]: {
        key: string;
        label: string;
        checked: boolean;
        partialChecked: boolean;
        uuid: string;
    };
};

interface ICategory {
    key: string;
    uuid: string;
    label: string;
    isActive: boolean;
    children?: [] | ICategory[];
}

interface ICompanyCategorySetting {
    categoryUuid: string;
}

interface ICompanyInfos {
    name: string;
    accountManager: string | null;
    uuid: string;
    totalActiveUsers: number;
}

const selectedCategoriesKeys = ref<SelectedTreeNodePrimeVueEventType>({});
const selectedCategoriesKeysMap = ref(new Map());
const categoriesKeysMapOnInit = ref(new Map());
const companyUuid = ref<string | null>(null);
const companyInfos = ref<ICompanyInfos>({
    name: "",
    accountManager: "",
    uuid: "",
    totalActiveUsers: 0,
});
const isLoadingCategories = ref<boolean>(true);
const isCategoriesSettingEmpty = ref<boolean>(true);
const isInitCategoriesStateChanges = ref<boolean>(false);
const categoriesLevel1Nodes = ref(Array<ICategory>());
const expandedCategoriesKeys = ref({});
const categoriesAndChildrenNodes = ref(Array<ICategory>());
const categoriesSettingNodes = ref(Array<ICompanyCategorySetting>());
const categoriesSettingMapNodes = ref(new Map());
const route = useRoute();
const router = useRouter();

watch(
    selectedCategoriesKeysMap,
    (newValue) => {
        selectedCategoriesKeys.value = transformMapToObject(newValue);
    },
    { deep: true }
);

watch(
    categoriesSettingNodes,
    (newValue) => {
        categoriesSettingMapNodes.value = transformObjectToMap(newValue);
    },
    { deep: true }
);

watch(companyUuid, () => {
    if (isCompanyUuidDefined()) {
        fetchCategoriesData();
    }
});

onMounted(() => {
    companyUuid.value = route.params.uuid as string;
});

function initSelectedCategoriesKeys() {
    prepareCategorySettingsData();
    hydrateCategoriesTreeview();
}

function prepareCategorySettingsData() {
    categoriesAndChildrenNodes.value.forEach((category) => {
        const catSettingMatchingCategory = categoriesSettingNodes.value.find(
            (setting) => setting.categoryUuid === category.uuid
        );

        if (catSettingMatchingCategory) {
            category.isActive = true;
        }

        categoriesKeysMapOnInit.value.set(category.key, {
            key: category.key,
            label: category.label,
            checked: category.isActive,
            partialChecked: false,
            uuid: category.uuid,
        });
    });
}

function hydrateCategoriesTreeview() {
    categoriesLevel1Nodes.value.forEach((category: ICategory) => {
        expandedCategoriesKeys.value[category.key] = true;
    });

    categoriesAndChildrenNodes.value.forEach((category: ICategory) => {
        if (!selectedCategoriesKeysMap.value.has(category.key)) {
            selectedCategoriesKeysMap.value.set(category.key, {
                checked: category.isActive,
                partialChecked: false,
            });
        }
    });
}

function onUpdateSelectedCategories(event) {
    isInitCategoriesStateChanges.value = true;
    selectedCategoriesKeysMap.value = transformObjectToMap(event);
}

async function saveCategories(event) {
    event.preventDefault();

    const checkedCategories = new Map<string, { uuid: string }>();
    selectedCategoriesKeysMap.value.forEach(
        (updatedNode: SelectedCategoryType, updatedKey: string) => {
            if (updatedNode.checked) {
                categoriesAndChildrenNodes.value.forEach((category: ICategory) => {
                    if (category.key === updatedKey) {
                        checkedCategories.set(updatedKey, { uuid: category.uuid });
                    }
                });
            }
        }
    );

    let dataToSave = {
        uuid: companyUuid.value,
        categories: Array.from(checkedCategories.values()),
    };

    await ApiService.patchWithoutSlug("company_settings", dataToSave)
        .then((response) => {
            swal.info("Les modifications ont bien été prises en compte");
            isInitCategoriesStateChanges.value = false;
        })
        .catch(() => {
            swal.error();
        });
}

async function confirmLeavePage() {
    return new Promise((resolve) => {
        swal
            .confirm(
                "Êtes-vous sûr de vouloir quitter cette page ? Les modifications non enregistrées seront perdues"
            )
            .then((result) => {
                resolve(result.isConfirmed);
            });
    });
}

onBeforeRouteLeave((to, from, next) => {
    if (isInitCategoriesStateChanges.value) {
        confirmLeavePage().then((shouldLeavePage) => {
            shouldLeavePage ? next() : next(false);
        });
    } else {
        next();
    }
});

async function fetchCategoriesData() {
    if (isCompanyUuidDefined()) {
        await ApiService.get("categories", {
            params: { lvl: 1, "order[lft]": "ASC" },
        })
            .then(async ({ data }) => {
                if (data.data.length > 0) {
                    data.data.forEach((category, index: number) => {
                        let id = index.toString();
                        let newCategory: ICategory = {
                            key: id,
                            uuid: category.uuid,
                            label: category.name,
                            isActive: false,
                            children: formatChildrenToCategory(category.children, id),
                        };
                        categoriesLevel1Nodes.value.push(newCategory);
                        categoriesAndChildrenNodes.value.push(newCategory);
                    });

                    const categoriesSettings = await ApiService.get(
                        "company_settings/company/" + companyUuid.value,
                        {}
                    );
                    if (categoriesSettings.data.data.length > 0) {
                        isCategoriesSettingEmpty.value = false;
                        categoriesSettings.data.data.forEach((categorySetting) => {
                            let newCategorySetting: ICompanyCategorySetting = {
                                categoryUuid: categorySetting.category.replace(
                                    /^\/api\/categories\//,
                                    ""
                                ),
                            };
                            categoriesSettingNodes.value.push(newCategorySetting);
                        });
                    }

                    const companyData = await ApiService.get(
                        "companies/" + companyUuid.value,
                        {}
                    );
                    companyInfos.value = companyData.data.data;
                    initSelectedCategoriesKeys();
                }
            })
            .catch(() => {
                swal.error("Une erreur a été détectée, veuillez re essayer 4.");
                return false;
            });
        isLoadingCategories.value = false;
    } else {
        router.push({ name: "categories-settings-list" });
    }
}

function formatChildrenToCategory(data, parentKey: string) {
    let childrenData = Array<ICategory>();
    data.forEach((item, index: number) => {
        let key = parentKey + "-" + index;
        let newCategory: ICategory = {
            key: key,
            uuid: item.uuid,
            label: item.name,
            isActive: false,
            children:
                item.children && item.children.length
                    ? formatChildrenToCategory(item.children, key)
                    : [],
        };
        childrenData.push(newCategory);
        categoriesAndChildrenNodes.value.push(newCategory);
    });

    return childrenData;
}

function isCompanyUuidDefined() {
    return companyUuid.value?.length;
}

function transformMapToObject(mapObject) {
    return Object.fromEntries(mapObject);
}

function transformObjectToMap(object) {
    return new Map(Object.entries(object));
}
</script>

